import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CalendarRange } from "lucide-react";

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function weekDayOptions() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const todayKey = keyOf(now);
  const opts = [];
  for (let w = 0; w < 2; w++) {
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + w * 7 + i);
      const key = keyOf(d);
      opts.push({
        key,
        week: w,
        label: `${WEEKDAY_LABELS[i]} ${d.getDate()}.${d.getMonth() + 1}.`,
        isToday: key === todayKey,
      });
    }
  }
  return opts;
}

function keyOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// The list of weekday items for a given scope.
function DayItems({ task, scope, onPlanWeek }) {
  return weekDayOptions().map((opt, i) => (
    <div key={`${scope}-${opt.key}`}>
      {i === 7 && <DropdownMenuSeparator />}
      {i === 7 && (
        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Nächste Woche</div>
      )}
      <DropdownMenuItem
        onClick={() => onPlanWeek(task, opt.key, scope)}
        data-testid={`task-plan-week-${scope}-${task.id}-${opt.key}`}
      >
        {opt.label}{opt.isToday ? " (heute)" : ""}
      </DropdownMenuItem>
    </div>
  ));
}

// Inner body: general tasks let you pick Meine/Team; personal tasks go straight to mine.
function PlanBody({ task, onPlanWeek }) {
  const isGeneral = task.assigned_to_user_id === "general";
  if (!isGeneral) {
    return <DayItems task={task} scope="mine" onPlanWeek={onPlanWeek} />;
  }
  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid={`task-plan-week-mine-trigger-${task.id}`}>
          Meine Woche
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
          <DayItems task={task} scope="mine" onPlanWeek={onPlanWeek} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid={`task-plan-week-team-trigger-${task.id}`}>
          Team-Woche
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
          <DayItems task={task} scope="team" onPlanWeek={onPlanWeek} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

// Used inside an existing DropdownMenu (e.g. the task card kebab).
export function WeekPlanSub({ task, onPlanWeek }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid={`task-plan-week-trigger-${task.id}`}>
        <CalendarRange className="w-4 h-4 mr-2" />
        In Woche einplanen
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
        <PlanBody task={task} onPlanWeek={onPlanWeek} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

// Standalone button + dropdown (e.g. inside the task edit dialog).
export function WeekPlanButton({ task, onPlanWeek }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid={`task-plan-week-btn-${task.id}`}>
          <CalendarRange className="w-4 h-4 mr-2" />
          In Woche einplanen
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        <PlanBody task={task} onPlanWeek={onPlanWeek} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
